import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import {
  calcularTotais,
  dadosEmpresaInicial,
  decimalBR,
  formatarCodigo,
  formatarDataBR,
  linhaTotal,
  moeda,
  numerarItensProposta,
  numero,
  propostaComDefaults,
  textoValidadeProposta,
  valorMonetarioPorExtenso,
} from "./propostaComercialModel";
import "./RelatorioPropostaComercialPrint.css";
import LogoRelatorio, { useMarcaDaguaRelatorio } from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";
import { db } from "../Config/firebase";

async function paginarPreviaProposta(frame) {
  const documento = frame.contentDocument;
  if (!documento) return;
  await documento.fonts?.ready;
  await Promise.all(Array.from(documento.images).map(imagem => imagem.decode?.().catch(() => {})));
  if (!frame.isConnected || frame.contentDocument !== documento) return;
  const origem = documento.querySelector(".proposta-pagina");
  if (!origem) return;
  const tabelaOriginal = origem.querySelector(".proposta-documento");
  const blocos = Array.from(tabelaOriginal.tBodies).flatMap(tbody =>
    Array.from(tbody.rows).flatMap(linha => Array.from(linha.cells[0].children))
  );
  origem.remove();
  let pagina;
  let conteudo;
  let quantidade = 0;

  function novaPagina() {
    pagina = origem.cloneNode(false);
    const tabela = tabelaOriginal.cloneNode(false);
    tabela.appendChild(tabelaOriginal.tHead.cloneNode(true));
    conteudo = tabela.createTBody().insertRow().insertCell();
    conteudo.className = "proposta-conteudo";
    pagina.appendChild(tabela);
    documento.body.appendChild(pagina);
    quantidade += 1;
    pagina.dataset.pagina = `Página ${quantidade}`;
  }

  function ultrapassou() {
    const limite = pagina.getBoundingClientRect().bottom -
      parseFloat(frame.contentWindow.getComputedStyle(pagina).paddingBottom) - 2;
    return conteudo.getBoundingClientRect().bottom > limite;
  }

  function adicionar(bloco) {
    const tinhaConteudo = conteudo.children.length > 0;
    conteudo.appendChild(bloco);
    if (ultrapassou() && tinhaConteudo) {
      bloco.remove();
      novaPagina();
      conteudo.appendChild(bloco);
    }
    if (ultrapassou()) throw new Error("Um bloco da proposta excede a altura útil de uma folha A4. Reduza o texto desse bloco para visualizar as páginas.");
  }

  novaPagina();
  let titulos = [];
  for (const bloco of blocos) {
    if (bloco.matches(".proposta-secao, .proposta-subsecao, .proposta-cordialmente")) {
      titulos.push(bloco.cloneNode(true));
      continue;
    }
    if (bloco.matches(".proposta-tabela-bloco")) {
      let grupo;
      let corpo;
      const criarGrupo = () => {
        grupo = bloco.cloneNode(true);
        corpo = grupo.querySelector("tbody");
        corpo.replaceChildren();
        return grupo;
      };
      const conjunto = documento.createElement("div");
      conjunto.append(...titulos, criarGrupo());
      titulos = [];
      const linhas = Array.from(bloco.querySelector("tbody").rows);
      for (let indice = 0; indice < linhas.length; indice += 1) {
        const linha = linhas[indice].cloneNode(true);
        corpo.appendChild(linha);
        if (indice === 0) {
          adicionar(conjunto);
        } else if (ultrapassou()) {
          linha.remove();
          novaPagina();
          criarGrupo();
          corpo.appendChild(linha);
          adicionar(grupo);
        }
      }
    } else {
      const conjunto = documento.createElement("div");
      conjunto.append(...titulos, bloco.cloneNode(true));
      titulos = [];
      adicionar(conjunto);
    }
  }
  if (titulos.length) {
    const conjunto = documento.createElement("div");
    conjunto.append(...titulos);
    adicionar(conjunto);
  }
  frame.style.height = `${documento.documentElement.scrollHeight}px`;
}

export default function PropostaPrint({ proposta, onVoltar, autoPrint = false }) {
  const marcaDaguaUrl = useMarcaDaguaRelatorio();
  const jaImprimiuRef = useRef(false);
  const origemPreviaRef = useRef(null);
  const framePreviaRef = useRef(null);
  const [htmlPrevia, setHtmlPrevia] = React.useState("");
  const [previaPronta, setPreviaPronta] = React.useState(false);
  const [erroPrevia, setErroPrevia] = React.useState("");
  const [dadosEmpresaTenant, setDadosEmpresaTenant] = React.useState(dadosEmpresaInicial);
  const [carregandoDadosEmpresa, setCarregandoDadosEmpresa] = React.useState(true);
  const propostaNumerada = numerarItensProposta(propostaComDefaults({
    ...proposta,
    dadosEmpresa: dadosEmpresaTenant,
  }));
  useNomeArquivoPdf(formatarCodigo(propostaNumerada.codigo));
  const totais = calcularTotais(propostaNumerada);
  const possuiDesconto = numero(propostaNumerada.descontoPercentual) > 0;
  const modeloModerno = propostaNumerada.modeloRelatorio === "modelo2";
  const possuiDadosCadastrais = propostaNumerada.dadosEmpresa?.preenchido === true;
  const possuiAssinatura = Boolean(
    propostaNumerada.dadosEmpresa?.assinaturaUrl ||
    propostaNumerada.dadosEmpresa?.nomeResponsavel ||
    propostaNumerada.dadosEmpresa?.empresaAssinatura
  );

  useEffect(() => {
    let cancelado = false;

    async function carregarDadosEmpresaTenant() {
      const tenantId = String(proposta?.tenantId || localStorage.getItem("tenantId") || "").trim();
      if (!tenantId) {
        if (!cancelado) setCarregandoDadosEmpresa(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "tenant_branding", tenantId));
        if (!cancelado) {
          setDadosEmpresaTenant({
            ...dadosEmpresaInicial,
            ...(snap.exists() ? snap.data()?.dadosPropostaComercial || {} : {}),
          });
        }
      } catch (error) {
        console.error("Erro ao carregar dados cadastrais da proposta:", error);
      } finally {
        if (!cancelado) setCarregandoDadosEmpresa(false);
      }
    }

    carregarDadosEmpresaTenant();
    return () => {
      cancelado = true;
    };
  }, [proposta?.tenantId]);

  useEffect(() => {
    if (carregandoDadosEmpresa || !origemPreviaRef.current) return;
    setPreviaPronta(false);
    setErroPrevia("");
    const regras = Array.from(document.styleSheets).flatMap(folha => {
      try {
        return Array.from(folha.cssRules).filter(regra => regra.cssText.includes(".proposta-")).map(regra => regra.cssText);
      } catch {
        return [];
      }
    }).join("\n").replace(/@media print/g, "@media all");
    setHtmlPrevia(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><style>${regras}
      @page { size: A4 portrait; margin: 0; }
      html, body { background: #e8edf3 !important; }
      .proposta-pagina { display: block !important; position: relative; margin: 0 auto 28px !important; }
      .proposta-pagina::after { content: attr(data-pagina); position: absolute; bottom: 3mm; right: 6mm; font: 9px Arial; color: #64748b; }
      @media print {
        html, body { background: #fff !important; }
        .proposta-pagina { margin: 0 !important; break-after: page; page-break-after: always; }
        .proposta-pagina:last-child { break-after: auto; page-break-after: auto; }
      }
    </style></head><body>${origemPreviaRef.current.outerHTML}</body></html>`);
  }, [proposta, dadosEmpresaTenant, carregandoDadosEmpresa, marcaDaguaUrl]);

  async function prepararPrevia() {
    const frame = framePreviaRef.current;
    const documento = frame?.contentDocument;
    try {
      await paginarPreviaProposta(frame);
      if (!frame?.isConnected || frame.contentDocument !== documento) return;
      frame.contentDocument.title = formatarCodigo(proposta.codigo);
      setPreviaPronta(true);
    } catch (error) {
      if (!frame?.isConnected || frame.contentDocument !== documento) return;
      setErroPrevia(error.message || "Não foi possível montar a prévia de impressão.");
    }
  }

  function imprimirPrevia() {
    if (!previaPronta) return;
    framePreviaRef.current?.contentWindow?.focus();
    framePreviaRef.current?.contentWindow?.print();
  }

  useEffect(() => {
    if (!autoPrint || !previaPronta || jaImprimiuRef.current) return;

    jaImprimiuRef.current = true;
    const frame = window.requestAnimationFrame(imprimirPrevia);

    return () => window.cancelAnimationFrame(frame);
  }, [autoPrint, previaPronta]);

  return (
    <>
      <Box className="proposta-print-actions">
        <button className="btn-voltar" onClick={onVoltar}>Voltar</button>
        <button className="btn-imprimir" onClick={imprimirPrevia} disabled={!previaPronta}>Imprimir</button>
      </Box>

      {!previaPronta && <Box role="status" sx={{ p: 3 }}>{erroPrevia || "Preparando páginas da proposta..."}</Box>}
      {htmlPrevia && <iframe ref={framePreviaRef} title="Páginas da proposta comercial" className="proposta-previa-paginada" srcDoc={htmlPrevia} onLoad={prepararPrevia} style={{ visibility: previaPronta ? "visible" : "hidden" }} />}

    <Box
      className={`proposta-pagina${modeloModerno ? " proposta-pagina--moderna" : ""}`}
      ref={origemPreviaRef}
      style={{ display: "none", "--marca-dagua-proposta": `url("${marcaDaguaUrl}")` }}
    >
  <table className="proposta-documento">
    <thead>
      <tr className="proposta-cabecalho-margem-topo">
        <td aria-hidden="true"></td>
      </tr>
      <tr>
        <td>
          {modeloModerno ? (
            <header className="proposta-moderna-cabecalho">
              <div className="proposta-moderna-logo">
                <div><LogoRelatorio alt="Logo da empresa" /></div>
              </div>
              <div className="proposta-moderna-titulo">
                <strong>{propostaNumerada.tituloRelatorio || "Proposta comercial"}</strong>
                <span>Sistema de Gestão da Qualidade</span>
              </div>
              <div className="proposta-moderna-codigo">
                <strong>{formatarCodigo(propostaNumerada.codigo)}</strong>
                <span>{formatarDataBR(propostaNumerada.data)}</span>
                <small>{propostaNumerada.rq || "RQ 6.4"} · REV. {propostaNumerada.revisao || "00"}</small>
              </div>
            </header>
          ) : (
          <table className="proposta-cabecalho">
            <tbody>
              <tr>
                <td rowSpan={3} className="proposta-logo">
                  <LogoRelatorio alt="Logo da empresa"  />
                </td>

                <td rowSpan={3} className="proposta-titulo">
                  <div>SISTEMA DE GESTÃO DA QUALIDADE</div>
                  <strong>{propostaNumerada.tituloRelatorio || "Proposta comercial"}</strong>
                </td>

                <td>{propostaNumerada.rq || "RQ 6.4"}</td>
              </tr>

              <tr>
                <td>REVISÃO {propostaNumerada.revisao || "00"}</td>
              </tr>

              <tr>
                <td>{formatarDataBR(propostaNumerada.data)}</td>
              </tr>

              <tr>
                <td className="proposta-label">DATA:</td>
                <td />
                <td>CÓDIGO: {formatarCodigo(propostaNumerada.codigo)}</td>
              </tr>
            </tbody>
          </table>
          )}

          {!modeloModerno && <table className="proposta-info">
            <tbody>
              <tr>
                <td className="proposta-info-label">Interessado</td>
                <td>{propostaNumerada.interessado || "-"}</td>
              </tr>

              <tr>
                <td className="proposta-info-label">Empreendimento</td>
                <td>{propostaNumerada.empreendimento || "-"}</td>
              </tr>
            </tbody>
          </table>}
        </td>
      </tr>
    </thead>

    <tbody>
  <tr>
    <td className="proposta-conteudo">

      {modeloModerno ? (
        <section className="proposta-moderna-resumo">
          <div><strong>Interessado</strong><span>{propostaNumerada.interessado || "-"}</span></div>
          <div><strong>Empreendimento</strong><span>{propostaNumerada.empreendimento || "-"}</span></div>
          <div>
            <strong>Contato</strong>
            <span>{propostaNumerada.destinatario || "-"}</span>
            <small>{[propostaNumerada.email, propostaNumerada.telefone].filter(Boolean).join(" · ") || "-"}</small>
          </div>
        </section>
      ) : (
      <table className="proposta-contato">
          <tbody>
            <tr>
              <th>Destinatário(s):</th>
              <th>e-mail</th>
              <th>Telefone</th>
            </tr>
            <tr>
              <td>{propostaNumerada.destinatario || "-"}</td>
              <td>{propostaNumerada.email || "-"}</td>
              <td>{propostaNumerada.telefone || "-"}</td>
            </tr>
          </tbody>
        </table>
      )}

        <p><strong>{propostaNumerada.textos?.saudacao ?? "Prezado(a),"}</strong></p>
        <p>{propostaNumerada.textos?.apresentacao}</p>

        <div className="proposta-secao">1. VALOR DO SERVIÇO.</div>
        {(propostaNumerada.tabelas || []).map((tabela) => {
          const totalTabela = (tabela.itens || []).reduce((total, item) => total + linhaTotal(item), 0);
          const mostrarSubtotal = totalTabela > 0 && tabela.exibirSubtotal !== false;

          return (
            <Box key={tabela.id || tabela.titulo} className="proposta-tabela-bloco">
              <p><strong>{tabela.titulo}</strong> - {tabela.subtitulo}</p>
              <table className="proposta-servicos">
                <thead>
                  <tr>
                    <th>ITEM</th>
                    <th>UND</th>
                    <th>QTE</th>
                    <th>DESCRIÇÃO</th>
                    <th>P. Unit (R$)</th>
                    <th>P. Total (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {(tabela.itens || []).map((item, index) => (
                    <tr key={`${tabela.id}-${index}`}>
                      <td>{item.item || `${index + 1}`}</td>
                      <td>{item.unidade || ""}</td>
                      <td>{String(item.quantidade ?? "").trim() ? decimalBR(item.quantidade) : "-"}</td>
                      <td>
                        <div>{item.descricao}</div>
                        {item.detalhesServico && (
                          <div className="proposta-servico-detalhes">{item.detalhesServico}</div>
                        )}
                      </td>
                      <td>{numero(item.valorUnitario) ? moeda(numero(item.valorUnitario)) : ""}</td>
                      <td>{linhaTotal(item) ? moeda(linhaTotal(item)) : ""}</td>
                    </tr>
                  ))}
                  <tr className="proposta-subtotal">
                    <td colSpan={5}>{mostrarSubtotal ? `SUB-TOTAL: ${valorMonetarioPorExtenso(totalTabela)}` : "\u00a0"}</td>
                    <td>{mostrarSubtotal ? moeda(totalTabela) : ""}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          );
        })}

        <table className="proposta-totais">
          <tbody>
            <tr><td>TOTAL DA PROPOSTA: {valorMonetarioPorExtenso(totais.subtotal)}.</td><td>{moeda(totais.subtotal)}</td></tr>
            {possuiDesconto && <tr><td>DESCONTO {decimalBR(propostaNumerada.descontoPercentual)}%:</td><td>{moeda(totais.desconto)}</td></tr>}
            <tr className="proposta-total-final">
              <td>
                {possuiDesconto ? "TOTAL DA PROPOSTA COM DESCONTO:" : "TOTAL FINAL DA PROPOSTA:"}{" "}
                {valorMonetarioPorExtenso(totais.total)}.
              </td>
              <td>{moeda(totais.total)}</td>
            </tr>
          </tbody>
        </table>

        <div className="proposta-secao">2. OBSERVAÇÕES.</div>
        <p className="proposta-observacoes">{propostaNumerada.textos?.observacoes || "-"}</p>

        <div className="proposta-secao">3. PRAZO DE INÍCIO E ENTREGA DOS SERVIÇOS.</div>
        <div className="proposta-subsecao">3.1. Prazo de início.</div>
        <p>{propostaNumerada.textos?.prazoInicio}</p>
        <div className="proposta-subsecao">
          3.2. {String(propostaNumerada.textos?.tituloEntrega || "Entrega dos relatórios").trim().replace(/\.+$/, "")}.
        </div>
        <p>{propostaNumerada.textos?.entregaRelatorios}</p>

        <div className="proposta-secao">4. CRITÉRIO DE MEDIÇÃO E PAGAMENTO.</div>
        <p>{propostaNumerada.textos?.pagamento}</p>

        <div className="proposta-secao">5. VALIDADE DA PROPOSTA.</div>
        <p>{textoValidadeProposta(propostaNumerada.validadeProposta, propostaNumerada.textos?.validade)}</p>

        {possuiDadosCadastrais && (
          <>
            <div className="proposta-dados-bloco">
              <div className="proposta-secao">6. DADOS CADASTRAIS.</div>
              <table className="proposta-dados">
                <tbody>
                  {Object.entries({
                    "Razão Social": propostaNumerada.dadosEmpresa?.razaoSocial,
                    CNPJ: propostaNumerada.dadosEmpresa?.cnpj,
                    Endereço: propostaNumerada.dadosEmpresa?.endereco,
                    Bairro: propostaNumerada.dadosEmpresa?.bairro,
                    Cidade: propostaNumerada.dadosEmpresa?.cidade,
                    CEP: propostaNumerada.dadosEmpresa?.cep,
                    Contato: propostaNumerada.dadosEmpresa?.contato,
                    "Site/E-mail": propostaNumerada.dadosEmpresa?.siteEmail,
                  }).map(([label, valor]) => (
                    <tr key={label}><td>{label}</td><td>{valor || "-"}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            {possuiAssinatura && (
              <>
                <p className="proposta-cordialmente">Cordialmente,</p>
                <div className="proposta-assinatura">
                  {propostaNumerada.dadosEmpresa?.assinaturaUrl && (
                    <img src={propostaNumerada.dadosEmpresa.assinaturaUrl} alt="Assinatura" />
                  )}
                  {propostaNumerada.dadosEmpresa?.nomeResponsavel && (
                    <strong>{propostaNumerada.dadosEmpresa.nomeResponsavel}</strong>
                  )}
                  {propostaNumerada.dadosEmpresa?.empresaAssinatura && (
                    <span>{propostaNumerada.dadosEmpresa.empresaAssinatura}</span>
                  )}
                </div>
              </>
            )}
          </>
        )}

      </td>
    </tr>
    <tr className="proposta-aceite-linha">
      <td>
        <div className="proposta-aceite">
          <strong>ACEITE DA PROPOSTA</strong>
          <p>Declaro que estou de acordo com os termos da proposta e autorizo o início imediato dos trabalhos.</p>
          <p><strong>{propostaNumerada.interessado || "-"}</strong></p>
          <table>
            <colgroup>
              <col style={{ width: "45%" }} />
              <col style={{ width: "35%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <tbody>
              <tr>
                                <th>Nome do representante da empresa</th>
                <th>Assinatura</th>
                <th>Data</th>
              </tr>

              <tr>
                <td />
                <td />
                <td />
              </tr>
            </tbody>
          </table>
        </div>

      </td>
    </tr>
  </tbody>
</table>
</Box>
    </>
  );
}
